import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { Network, Shield, Zap, Activity, Server } from "lucide-react";
import { LanguageToggle } from "@/components/LanguageToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth/AuthProvider";
import { errorTitle, formatSupportId } from "@/lib/api/error-display";
import { cn } from "@/lib/utils";

type LoginForm = { email: string; password: string };

const PANEL_GLYPHS = [
  { Icon: Shield, className: "left-[14%] top-[18%] animate-float-slow" },
  { Icon: Zap, className: "right-[18%] top-[30%] animate-float-slower" },
  { Icon: Server, className: "left-[22%] bottom-[26%] animate-float-slower" },
  { Icon: Activity, className: "right-[14%] bottom-[16%] animate-float-slow" },
] as const;

export default function Login() {
  const { t } = useTranslation();
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const reduced = useReducedMotion();
  const [formError, setFormError] = React.useState<{
    title: string;
    supportId?: string;
  } | null>(null);

  const redirectTo =
    (location.state as { from?: string } | null)?.from ?? "/app";

  const loginSchema = z.object({
    email: z.string().email(t("auth.invalidEmail")),
    password: z.string().min(1, t("auth.passwordRequired")),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({ resolver: zodResolver(loginSchema) });

  if (status === "authenticated") {
    return <Navigate to={redirectTo} replace />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await login(values);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setFormError({
        title: errorTitle(err, t),
        supportId: formatSupportId(err, t),
      });
    }
  });

  const fieldVariants = {
    hidden: { opacity: 0, y: 16 },
    show: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: 0.1 + i * 0.08,
        ease: [0.21, 0.47, 0.32, 0.98] as const,
      },
    }),
  };

  return (
    <main className="grid min-h-full flex-1 lg:grid-cols-2">
      <section className="dark relative hidden overflow-hidden bg-background text-foreground lg:flex lg:flex-col lg:justify-between lg:p-12">
        <div
          aria-hidden="true"
          className="animate-orb-pulse absolute -left-24 top-1/3 size-[480px] rounded-full bg-primary/25 blur-[130px]"
        />
        <div aria-hidden="true" className="bg-glyph-grid absolute inset-0 opacity-50" />
        {PANEL_GLYPHS.map(({ Icon, className }, i) => (
          <span
            key={i}
            aria-hidden="true"
            className={cn("absolute text-foreground/10", className)}
          >
            <Icon className="size-10" strokeWidth={1.25} />
          </span>
        ))}

        <Link to="/" className="relative flex items-center gap-3">
          <span className="glow-primary flex size-10 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <Network className="size-5" aria-hidden="true" />
          </span>
          <span className="text-lg font-semibold tracking-tight">{t("brand")}</span>
        </Link>

        <motion.div
          initial={reduced ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="relative space-y-4"
        >
          <h1 className="text-balance text-4xl font-bold leading-tight tracking-tight">
            {t("auth.consoleTitle")}
          </h1>
        </motion.div>

        <p className="relative text-sm text-muted-foreground">
          {t("public.footer.copyright")}
        </p>
      </section>

      <section className="relative flex items-center justify-center px-6 py-16">
        <div className="absolute right-4 top-4 sm:right-6 sm:top-6">
          <LanguageToggle />
        </div>
        <div className="w-full max-w-sm">
          <motion.div
            initial={reduced ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.21, 0.47, 0.32, 0.98] }}
            className="mb-10 flex flex-col gap-3 lg:hidden"
          >
            <span className="glow-primary flex size-11 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Network className="size-5" aria-hidden="true" />
            </span>
            <p className="text-lg font-semibold tracking-tight text-foreground">
              {t("brand")}
            </p>
          </motion.div>

          <motion.div
            initial={reduced ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.21, 0.47, 0.32, 0.98] }}
            className="mb-8 space-y-1.5"
          >
            <h2 className="text-2xl font-bold tracking-tight text-foreground">
              {t("auth.signIn")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t("auth.welcomeBack")}
            </p>
          </motion.div>

          <form onSubmit={onSubmit} className="space-y-5" noValidate>
            <motion.div
              custom={0}
              variants={fieldVariants}
              initial={reduced ? false : "hidden"}
              animate="show"
              className="space-y-2"
            >
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                autoFocus
                className="h-11 transition-shadow focus-visible:glow-primary"
                {...register("email")}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </motion.div>

            <motion.div
              custom={1}
              variants={fieldVariants}
              initial={reduced ? false : "hidden"}
              animate="show"
              className="space-y-2"
            >
              <Label htmlFor="password">{t("auth.password")}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                className="h-11 transition-shadow focus-visible:glow-primary"
                {...register("password")}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </motion.div>

            {formError && (
              <motion.p
                initial={reduced ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-sm text-destructive"
                role="alert"
              >
                <span className="block">{formError.title}</span>
                {formError.supportId && (
                  <span className="mt-1 block text-xs text-muted-foreground">
                    {formError.supportId}
                  </span>
                )}
              </motion.p>
            )}

            <motion.div
              custom={2}
              variants={fieldVariants}
              initial={reduced ? false : "hidden"}
              animate="show"
            >
              <Button
                type="submit"
                size="lg"
                className="glow-primary h-11 w-full rounded-full text-base transition-shadow hover:glow-primary-strong"
                disabled={isSubmitting}
              >
                {isSubmitting ? t("auth.signingIn") : t("auth.signIn")}
              </Button>
            </motion.div>
          </form>
        </div>
      </section>
    </main>
  );
}
